import { useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { useFiltersStore } from '@/store/use-filters-store';
import { useUserInfoStore } from '@/store/use-user-info-store';

interface CollapsableListState {
  expandedItemIds: number[];
  page: number;
  pageSize: number;
}

const DEFAULT_STATE: CollapsableListState = {
  expandedItemIds: [],
  page: 1,
  pageSize: 10,
};

export function useCollapsableList<T>() {
  const { selectedAccount } = useUserInfoStore();
  const { collapsableList, setCollapsableList } = useFiltersStore();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const accountCode = selectedAccount?.code;
  const page = location.pathname;

  const getStoredState = (): CollapsableListState => {
    if (!accountCode) {
      return DEFAULT_STATE;
    }
    return collapsableList[accountCode]?.[page] || DEFAULT_STATE;
  };

  const storedState = getStoredState();

  const updateStateFromUrl = () => {
    if (!accountCode) {
      return;
    }

    const urlExpanded = searchParams.get('expanded');
    const urlPage = searchParams.get('page');
    const urlPageSize = searchParams.get('pageSize');

    const newState: CollapsableListState = {
      ...storedState,
      expandedItemIds: urlExpanded ? urlExpanded.split(',').map(Number) : storedState.expandedItemIds,
      page: urlPage ? Number(urlPage) : storedState.page,
      pageSize: urlPageSize ? Number(urlPageSize) : storedState.pageSize,
    };

    setCollapsableList(page, newState);
  };

  useEffect(() => {
    updateStateFromUrl();
  }, [location.pathname, accountCode, searchParams]);

  const handlePaginationChange = (newPage: number, newPageSize?: number) => {
    if (!accountCode) {
      return;
    }

    const newState: CollapsableListState = {
      ...storedState,
      page: newPage,
      ...(newPageSize ? { pageSize: newPageSize } : {}),
    };

    setCollapsableList(page, newState);
  };

  const toggleExpandItems = (items: (T & { id: number })[]) => {
    if (!accountCode) {
      return;
    }

    const newState: CollapsableListState = {
      ...storedState,
      expandedItemIds: storedState.expandedItemIds.length 
        ? [] 
        : items.map((item) => item.id),
    };

    setCollapsableList(page, newState);
  };

  const toggleExpandItem = (itemId: number) => {
    if (!accountCode) {
      return;
    }

    const expanded = storedState.expandedItemIds;
    const isExpanded = expanded.includes(itemId);

    const newState: CollapsableListState = {
      ...storedState,
      expandedItemIds: isExpanded
        ? expanded.filter((id) => id !== itemId)
        : [...expanded, itemId],
    };

    setCollapsableList(page, newState);
  };

  return {
    expandedItemIds: storedState.expandedItemIds,
    toggleExpandItems,
    toggleExpandItem,
    pagination: {
      page: storedState.page,
      pageSize: storedState.pageSize,
      handlePaginationChange,
    },
  };
}
